<template>
    <div class="w-full px-2">
        <div class="pin absolute bg-tertiary upload-helper text-center flex items-center justify-center rounded-lg" v-if="draggingFiles" @drop.prevent="filesDropped" @dragenter="overDropzone = true" @dragleave="overDropzone = false">
            <div class="dropzone" :class="{over: overDropzone}">
                <i class="fas fa-file-upload fa-4x block"></i>
                <h3 class="mt-5 mb-2">Upload {{ type }}s</h3>
                <p>Drop your new {{ type }} files here</p>
            </div>
        </div>
        <p class="mb-5 text-xs">Click the 'Add {{ type }}' button, or drag files on to this section to upload new media. Drag the tiles around to reorder them.</p>

        <transition-group class="flex flex-wrap -mx-2 gallery-container" name="list" tag="div">
            <div v-for="(card, i) in cards" :key="i + 0" :class="cardClass+(selectedItem === card ? ' invisible' : '')" class="draggable-tile bg-white" :data-index="i">
                <div class="flex flex-wrap -mx-2 text-sm" v-if="card.progress === undefined">
                    <div class="title w-1/2 mb-2 px-2">
                        {{ type }} #{{i+1}}
                    </div>
                    <div class="icons w-1/2 text-right px-2">
                        <a v-if="card.url !== null" :href="card.url.original ? card.url.original : card.url" target="_blank" download><i class="fas fa-download text-tertiary cursor-pointer mr-6 text-lg" title="Download"></i></a>
                        <i class="fas fa-trash-alt cursor-pointer text-lg" title="Delete" @click="deleteMedia(card)"></i>
                    </div>
                </div>
                <div class="flex flex-wrap -mx-2 text-sm" v-else-if="card.url === undefined">
                  <div class="title w-full mb-2 px-2">
                    We're currently duplicating the media
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 text-sm" v-else>
                    <div class="title w-full mb-2 px-2">
                        Uploading ({{ card.progress }}%)...
                    </div>
                </div>
                <div class="flex flex-col bg-grey-lighter border p-2 text-center">
                    <div class="img-preview cursor-move w-full h-24 text-red-dark flex flex-wrap content-center justify-center" v-if="card.url === null" @mousedown="dragStart(card, $event)" @touchstart="dragStart(card, $event)" :title="feedProvider ? `File not received from ${feedProvider.name}` : 'Media not available'">
                        <i class="fas fa-exclamation-triangle fa-3x"></i>
                    </div>
                    <div class="img-preview cursor-move w-full h-24" v-else-if="card.url && card.url.large" :style="{'background-image': `url('${card.url.large}')`}" @mousedown="dragStart(card, $event)" @touchstart="dragStart(card, $event)"></div>
                    <div class="img-preview cursor-move w-full h-24 text-tertiary flex flex-wrap content-center justify-center" v-else @mousedown="dragStart(card, $event)" @touchstart="dragStart(card, $event)">
                        <i class="fas fa-file fa-3x"></i>
                    </div>
                    <div class="caption">
                        <input type="text" v-model="card.caption" class="block w-full border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" placeholder="Enter Caption">
                    </div>
                </div>
            </div>
        </transition-group>

        <div class="flex flex-wrap -mx-2">
            <div class="w-full px-2 text-center">
                <label>
                    <div class="inline-block bg-transparent hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow cursor-pointer">
                        + Add {{ type }}
                    </div>
                    <input type="file" class="hidden" @change="handleFileUpload" :accept="accept" multiple/>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { closestEl } from '@/helpers'
import api from '@/http'
import http from 'axios'

export default {
  name: 'cms-gallery',
  props: {
    type: String,
    cardClass: String,
    cards: Array,
    files: File,
    mediaType: Number,
    accept: String,
    feedProvider: Object
  },
  data () {
    return {
      selectedItem: null,
      selectedEl: null,
      draggingFiles: false,
      overDropzone: false
    }
  },
  computed: {
    ...mapState(['currentProperty', 'selectedOffice'])
  },
  created () {
    document.querySelector('body').addEventListener('mouseup', this.dragEnd)
    document.querySelector('body').addEventListener('mousemove', this.drag)

    document.querySelector('body').addEventListener('touchend', this.dragEnd)
    document.querySelector('body').addEventListener('touchmove', this.drag)

    document.addEventListener('dragover', this.dragAndDropStart)
    document.addEventListener('dragleave', this.dragAndDropEnd)
    document.addEventListener('drop', this.dragAndDropEnd)
  },
  beforeDestroy () {
    document.querySelector('body').removeEventListener('mouseup', this.dragEnd)
    document.querySelector('body').removeEventListener('mousemove', this.drag)

    document.querySelector('body').removeEventListener('touchend', this.dragEnd)
    document.querySelector('body').removeEventListener('touchmove', this.drag)

    document.removeEventListener('dragover', this.dragAndDropStart)
    document.removeEventListener('dragleave', this.dragAndDropEnd)
    document.removeEventListener('drop', this.dragAndDropEnd)
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    deleteMedia (card) {
      let index = this.cards.indexOf(card)
      this.cards.splice(index, 1)
    },
    dragStart (item, e) {
      e.preventDefault()
      // Not a main button click
      if (!e.touches && e.button !== 0) {
        return
      }

      this.selectedItem = item
      let tile = closestEl(e.target, '.draggable-tile')
      let bounds = tile.getBoundingClientRect()

      // Create a clone
      this.selectedEl = tile.cloneNode(true)
      this.selectedEl.style.position = 'absolute'
      this.selectedEl.style.width = bounds.width + 'px'
      this.selectedEl.style.height = bounds.height + 'px'
      this.selectedEl.style.top = (bounds.top + window.scrollY) + 'px'
      this.selectedEl.style.left = (bounds.left + window.scrollX) + 'px'
      this.selectedEl.style.zIndex = '100'
      this.selectedEl.style.cursor = 'move'

      document.querySelector('#app').appendChild(this.selectedEl)
    },
    drag (e) {
      if (this.selectedEl) {
        e.preventDefault()
        let x = e.touches ? e.touches[0].pageX : e.pageX
        let y = e.touches ? e.touches[0].pageY : e.pageY
        let cX = e.touches ? e.touches[0].clientX : e.clientX
        let cY = e.touches ? e.touches[0].clientY : e.clientY

        let bounds = this.selectedEl.getBoundingClientRect()
        let nY = (y - (bounds.height / 2))
        let nX = (x - (bounds.width / 2))

        this.selectedEl.style.top = nY + 'px'
        this.selectedEl.style.left = nX + 'px'

        /*
        elementFromPoint is based on visual elements,
        This trick is to hide the dragged element so we can determine
        the element underneath and then show the element again rapidly.
        */
        let tile = null
        this.selectedEl.hidden = true
        let els = document.elementsFromPoint(cX, cY)
        this.selectedEl.hidden = false

        // We fetch all elements as .draggable-tile could be anywhere
        // in the DOM tree, so it's necessary to find a match.
        for (let i = 0; i < els.length; i++) {
          if (els[i].matches('.draggable-tile')) {
            tile = els[i]
            break
          }
        }

        // We want to call shiftOrder as least times as possible,
        // so we hold a reference to the element we just shifted to
        // save on cycles.
        if (tile && this.currentPointEl !== tile) {
          this.currentPointEl = tile
          this.shiftOrder(tile.getAttribute('data-index'))
        }
      }
    },
    dragEnd () {
      if (this.selectedEl) {
        document.querySelector('#app').removeChild(this.selectedEl)
      }

      // Resets
      this.selectedItem = null
      this.selectedEl = null
    },
    shiftOrder (b) {
      if (this.selectedItem) {
        let a = this.cards.indexOf(this.selectedItem)
        this.cards.splice(a, 1)
        this.cards.splice(b, 0, this.selectedItem)
      }
    },
    dragAndDropStart (e) {
      e.preventDefault()
      this.draggingFiles = true
    },
    dragAndDropEnd (e) {
      e.preventDefault()
      this.draggingFiles = false
    },
    filesDropped (e) {
      this.draggingFiles = false
      this.overDropzone = false

      if (e.dataTransfer.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          if (e.dataTransfer.items[i].kind !== 'file') {
            continue
          }

          this.uploadFile(e.dataTransfer.items[i].getAsFile())
        }
      } else {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          this.uploadFile(e.dataTransfer.files[i])
        }
      }
    },
    handleFileUpload (e) {
      for (let i = 0; i < e.target.files.length; i++) {
        this.uploadFile(e.target.files[i])
      }
    },
    async uploadFile (file) {
      // Validate
      let validTypes = this.accept.split(',')
      let valid = false
      for (let i = 0; i < validTypes.length; i++) {
        let type = validTypes[i].replace('*', '')
        if (file.type.indexOf(type) === 0) {
          valid = true
        }
      }
      if (!valid) {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Upload Failed',
          info: `${file.name} is not a valid format`
        })
        return
      }

      // Create the object
      let card = {
        'caption': file.name,
        'url': '',
        'cms_created': true,
        'progress': 0
      }
      this.cards.push(card)

      // Show a local image preview
      let reader = new FileReader()
      reader.onload = function (e) {
        card.url = {
          'large': e.target.result,
          'hd': e.target.result,
          'original': e.target.result
        }
      }
      reader.readAsDataURL(file)

      // Upload the image
      let filePath = `properties/${this.selectedOffice.network_id}/${this.selectedOffice.client_id}/${this.selectedOffice.id}/${this.currentProperty.id}`

      const extension = file.name.split('.').pop()
      const response = await api.post('signed-upload', {
        'disk': 's3',
        'extension': extension,
        'path': filePath,
        'visibility': 'public-read',
        'content_type': file.type
      })

      let s3File = response.data.key
      let headers = response.data.headers
      if ('Host' in headers) {
        delete headers.Host
      }

      await http.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
          card.progress = Math.ceil(progressEvent.loaded / progressEvent.total * 100)
        }
      })

      let data = {
        filePath: s3File,
        extension: extension,
        caption: file.name
      }

      api.post(`/properties/${this.currentProperty.id}/upload-media/${this.mediaType}`, data)
        .then((response) => {
          let index = this.cards.indexOf(card)
          this.cards.splice(index, 1, response.data)
        }, () => {
          let index = this.cards.indexOf(card)
          this.cards.splice(index, 1)
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Upload Failed',
            info: file.name
          })
        })
    }
  }
}
</script>

<style type="scss" scoped>
.img-preview {
    background: no-repeat center / contain;
}
.draggable-tile {
  user-select: none;
}
.list-move {
  transition: transform .3s;
}
.list-enter, .list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.list-leave-active {
  position: absolute;
}
.upload-helper {
    background-color: rgba(255,255,255,.95);
    z-index: 2;
}
.dropzone {
    transform: scale(1);
    transition: transform .3s;
    pointer-events: none;
}
.dropzone.over {
    transform: scale(1.1)
}
</style>
